import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/* ══════════════════════════════════════════════
   LANDING VARIANT E — "Scrollytelling"
   סיפור נגלל: hero קינטי עם מילים שעולות בזו אחר זו,
   ואז פרק דביק (sticky) — הגלילה מחליפה צילומי מסך
   אמיתיים ופרקי טקסט, עם פס התקדמות ומונה פרקים.
   ══════════════════════════════════════════════ */

const BG = '#0B0F1C';
const ACCENT = '#4ADE80'; /* signal green */

const CHAPTERS = [
  {
    k: '01',
    title: 'כל שקל נספר.',
    body: '₪307,547 תרומות, 290 רשומות, קבלה אוטומטית לכל אחת — מסך ההכנסות מרכז הכל: אשראי, ביט, העברות ומזומן.',
    img: '/screenshots/income-hero.png',
    tag: 'הכנסות ותרומות',
  },
  {
    k: '02',
    title: 'תקציב שמחזיק את עצמו.',
    body: 'מתכננים שנה מראש — סעיף־סעיף, חודש־חודש. המערכת משווה תכנון מול ביצוע ומתריעה לפני שחורגים.',
    img: '/screenshots/budget.png',
    tag: 'תקציב שנתי',
  },
  {
    k: '03',
    title: 'רואים את הכסף זז.',
    body: 'הכנסות מול הוצאות על ציר זמן חי — 7, 30 או 90 יום אחורה. בלי לחכות לרואה החשבון בסוף הרבעון.',
    img: '/screenshots/money-flow.png',
    tag: 'תנועת כספים',
  },
];

function useScrollProgress() {
  const ref = useRef<HTMLDivElement>(null);
  const [p, setP] = useState(0);
  useEffect(() => {
    const onScroll = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      const done = Math.min(Math.max(-r.top, 0), Math.max(total, 1));
      setP(total > 0 ? done / total : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return { ref, p };
}

const HERO_WORDS = ['תרומות.', 'קמפיינים.', 'תקציב.', 'הכל.', 'במקום', 'אחד.'];

const LandingVariantE: React.FC = () => {
  const { ref: storyRef, p } = useScrollProgress();
  const idx = Math.min(CHAPTERS.length - 1, Math.floor(p * CHAPTERS.length));

  return (
    <div dir="rtl" className="min-h-screen font-heebo text-white overflow-x-clip" style={{ background: BG }}>
      <style>{`
        @keyframes eWordUp { from { opacity: 0; transform: translateY(110%); } to { opacity: 1; transform: translateY(0); } }
        @keyframes eBlink { 0%,100% { opacity: 1; } 50% { opacity: 0.25; } }
        @keyframes eScrollHint { 0%,100% { transform: translateY(0); opacity: 0.9; } 50% { transform: translateY(8px); opacity: 0.4; } }
        .e-word { display: inline-block; overflow: hidden; vertical-align: bottom; }
        .e-word > span { display: inline-block; animation: eWordUp 0.7s cubic-bezier(0.22,1,0.36,1) both; }
      `}</style>

      {/* ── minimal header ── */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md" style={{ background: 'rgba(11,15,28,0.7)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="font-black text-xl tracking-tight">
            עמוטק<span style={{ color: ACCENT }}>_</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm font-semibold text-white/70 hover:text-white transition-colors">כניסה</Link>
            <Link to="/register" className="text-sm font-bold px-4 py-2 rounded-lg transition-transform hover:scale-105" style={{ background: ACCENT, color: BG }}>
              התחל בחינם
            </Link>
          </div>
        </div>
      </header>

      {/* ━━━━ KINETIC HERO ━━━━ */}
      <section className="relative min-h-screen flex flex-col justify-center px-6 pt-14">
        {/* scanline accent */}
        <div className="absolute inset-x-0 top-1/2 h-px pointer-events-none" style={{ background: 'linear-gradient(90deg, transparent, rgba(74,222,128,0.25), transparent)' }} />
        <div className="max-w-6xl mx-auto w-full">
          <p className="text-sm font-bold tracking-[0.3em] mb-8" style={{ color: ACCENT }}>
            מערכת ההפעלה של העמותה שלכם <span style={{ animation: 'eBlink 1.2s steps(1) infinite' }}>▮</span>
          </p>
          <h1 className="font-black leading-[1.04]" style={{ fontSize: 'clamp(48px, 9vw, 120px)' }}>
            {HERO_WORDS.map((w, i) => (
              <span key={i} className="e-word ml-5">
                <span style={{ animationDelay: `${200 + i * 140}ms`, color: i === 3 ? ACCENT : undefined }}>{w}</span>
              </span>
            ))}
          </h1>
          <div className="mt-10 flex flex-wrap items-center gap-6">
            <Link
              to="/register"
              className="px-8 py-4 text-lg font-bold rounded-xl transition-all duration-300 hover:scale-[1.04]"
              style={{ background: ACCENT, color: BG, boxShadow: '0 10px 40px rgba(74,222,128,0.35)' }}
            >
              פתח חשבון בחינם ←
            </Link>
            <p className="text-white/50 text-sm">7 ימי ניסיון · ללא כרטיס אשראי · מיוצר בישראל 🇮🇱</p>
          </div>
        </div>
        <div className="absolute bottom-8 inset-x-0 flex flex-col items-center gap-2 text-white/50 text-xs tracking-widest" style={{ animation: 'eScrollHint 2s ease-in-out infinite' }}>
          <span>גללו כדי לראות את המערכת</span>
          <span>↓</span>
        </div>
      </section>

      {/* ━━━━ STICKY STORY ━━━━ */}
      <div ref={storyRef} style={{ height: '340vh' }} className="relative">
        <div className="sticky top-0 h-screen flex items-center overflow-hidden">
          <div className="max-w-6xl mx-auto w-full px-6 grid lg:grid-cols-[1fr_1.4fr] gap-10 items-center">

            {/* text side */}
            <div className="relative order-2 lg:order-1 min-h-[240px]">
              {/* ghost chapter number */}
              <span aria-hidden="true" className="absolute -top-24 right-0 font-black select-none pointer-events-none" style={{ fontSize: 180, color: 'rgba(255,255,255,0.04)', lineHeight: 1 }}>
                {CHAPTERS[idx].k}
              </span>
              {CHAPTERS.map((c, i) => (
                <div key={c.k} className="transition-all duration-500 absolute inset-0" style={{ opacity: idx === i ? 1 : 0, transform: `translateY(${idx === i ? 0 : idx > i ? -18 : 18}px)`, pointerEvents: idx === i ? 'auto' : 'none' }}>
                  <span className="inline-block px-3 py-1 rounded-full text-xs font-bold mb-5" style={{ background: 'rgba(74,222,128,0.12)', color: ACCENT, border: '1px solid rgba(74,222,128,0.3)' }}>
                    {c.k} · {c.tag}
                  </span>
                  <h2 className="text-3xl sm:text-5xl font-black mb-5 leading-tight">{c.title}</h2>
                  <p className="text-lg text-white/60 leading-relaxed max-w-md">{c.body}</p>
                </div>
              ))}

              {/* progress rail */}
              <div className="absolute -right-6 top-0 bottom-0 hidden lg:flex flex-col items-center gap-0" aria-hidden="true">
                <div className="w-px flex-1" style={{ background: 'rgba(255,255,255,0.12)' }} />
              </div>
            </div>

            {/* image side */}
            <div className="relative order-1 lg:order-2">
              <div className="absolute -inset-8 rounded-full blur-[70px] pointer-events-none transition-colors duration-700"
                   style={{ background: idx === 0 ? 'rgba(59,130,246,0.18)' : idx === 1 ? 'rgba(34,197,94,0.16)' : 'rgba(245,158,11,0.15)' }} />
              <div className="relative rounded-2xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 50px 100px -30px rgba(0,0,0,0.8)' }}>
                {/* chrome */}
                <div dir="ltr" className="flex items-center gap-2 px-4 py-2.5" style={{ background: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                    <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
                    <span className="w-3 h-3 rounded-full bg-[#28c840]" />
                  </div>
                  <div className="flex-1 flex justify-center">
                    <div className="w-full max-w-[220px] rounded-md text-[11px] text-center py-1 font-medium" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)' }}>
                      app.amutech.co.il
                    </div>
                  </div>
                  <div className="w-10" />
                </div>
                {/* stacked crossfading screenshots */}
                <div className="relative" style={{ aspectRatio: '2 / 1', background: '#fff' }}>
                  {CHAPTERS.map((c, i) => (
                    <img
                      key={c.k}
                      src={c.img}
                      alt={c.tag + ' — עמוטק'}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-contain object-center transition-all duration-700"
                      style={{ opacity: idx === i ? 1 : 0, transform: `scale(${idx === i ? 1 : 1.05})` }}
                    />
                  ))}
                </div>
              </div>

              {/* chapter dots */}
              <div className="flex justify-center gap-2.5 mt-6">
                {CHAPTERS.map((c, i) => (
                  <span key={c.k} className="h-1.5 rounded-full transition-all duration-500" style={{ width: idx === i ? 34 : 10, background: idx === i ? ACCENT : 'rgba(255,255,255,0.2)' }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ━━━━ FINAL CTA ━━━━ */}
      <section className="relative px-6 py-32 text-center">
        <div className="absolute inset-x-0 top-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)' }} />
        <h2 className="font-black leading-tight mx-auto max-w-3xl" style={{ fontSize: 'clamp(34px, 6vw, 64px)' }}>
          הסיפור הבא שנגלול כאן —
          <br />
          <span style={{ color: ACCENT }}>של העמותה שלכם.</span>
        </h2>
        <p className="mt-6 text-white/50 text-lg">7 ימי ניסיון חינם · ללא כרטיס אשראי · ביטול בכל עת</p>
        <Link
          to="/register"
          className="inline-block mt-10 px-10 py-4 text-xl font-bold rounded-xl transition-all duration-300 hover:scale-[1.04]"
          style={{ background: ACCENT, color: BG, boxShadow: '0 10px 40px rgba(74,222,128,0.35)' }}
        >
          פתח חשבון בחינם ←
        </Link>
      </section>

      <footer className="px-6 py-6 text-sm text-white/40 flex flex-col sm:flex-row items-center justify-between gap-2 max-w-6xl mx-auto" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <p>© 2026 עמוטק – פתרונות דיגיטליים לעמותות בע"מ</p>
        <div className="flex gap-4">
          <Link to="/terms" className="hover:text-white transition-colors">תנאי שימוש</Link>
          <Link to="/privacy" className="hover:text-white transition-colors">פרטיות</Link>
          <Link to="/accessibility" className="hover:text-white transition-colors">נגישות</Link>
        </div>
      </footer>
    </div>
  );
};

export default LandingVariantE;
